import { useEffect, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { UnscheduledEvent } from '../../types';
import { AtaCodeRecord, formatAtaCode } from '../../services/ataCodeService';
import {
  AtaClassificationRecommendation,
  AtaClassificationRequest,
  classifyAtaRecommendation,
} from '../../services/ataClassificationService';
import { FleetRecord, getAircraftTypeByTail, getOperatorByTail } from '../../services/fleetService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('UseAtaClassification');

interface Defect {
  id: number;
  text: string;
}

interface UseAtaClassificationOptions {
  isOpen: boolean;
  mode: 'view' | 'edit' | 'create';
  recordType: string;
  triageRecommendationDefectIds: number[];
  formData: UnscheduledEvent | null;
  defects: Defect[];
  fleetData: FleetRecord[];
  availableAtaCodes: AtaCodeRecord[];
  selectedChapters: Record<number, string>;
  selectedSections: Record<number, string>;
  setSelectedChapters: Dispatch<SetStateAction<Record<number, string>>>;
  setSelectedSections: Dispatch<SetStateAction<Record<number, string>>>;
}

interface UseAtaClassificationResult {
  pendingDefectIds: number[];
  unconfirmedDefectIds: number[];
  failedDefectIds: number[];
  settledDefectIds: number[];
  acknowledgeRecommendation: (defectId: number) => void;
  discardRecommendation: (defectId: number) => void;
  requestRecommendations: (defectIds: number[]) => boolean;
}

interface ClassificationTask {
  defectId: number;
  request: AtaClassificationRequest;
  availableAtaCodes: AtaCodeRecord[];
  isActive: () => boolean;
  onRecommendation: (recommendation: AtaClassificationRecommendation) => void;
  onFailure: () => void;
  onFinished: () => void;
}

function containsRecommendation(
  codes: AtaCodeRecord[],
  recommendation: AtaClassificationRecommendation,
): boolean {
  return codes.some(
    (code: AtaCodeRecord): boolean =>
      code.ata_code_id === recommendation.ataCodeId &&
      code.ata_chapter === recommendation.chapter &&
      code.ata_sub_chapter === recommendation.section,
  );
}

function removeDefectId(previous: number[], defectId: number): number[] {
  return previous.filter((candidateId: number): boolean => candidateId !== defectId);
}

function addDefectId(previous: number[], defectId: number): number[] {
  return previous.includes(defectId) ? previous : [...previous, defectId];
}

function buildRequest(
  defect: Defect,
  formData: UnscheduledEvent,
  carrier: string,
  aircraftType: string,
  selectedChapters: Record<number, string>,
  selectedSections: Record<number, string>,
): AtaClassificationRequest {
  const inputAtaCode = formatAtaCode(
    selectedChapters[defect.id] || '',
    selectedSections[defect.id] || '',
  );
  return {
    tailNumber: formData.tailNumber,
    carrier,
    aircraftType,
    defectDescription: defect.text.trim(),
    station: formData.station || '',
    currentStatus: formData.status || '',
    inputAtaCode: inputAtaCode === '—' ? '' : inputAtaCode,
  };
}

async function runClassificationTask(task: ClassificationTask): Promise<void> {
  try {
    const response = await classifyAtaRecommendation(task.request);
    if (!task.isActive()) return;
    const recommendation = response.recommendation;
    if (!recommendation || !containsRecommendation(task.availableAtaCodes, recommendation)) {
      task.onFailure();
      return;
    }
    task.onRecommendation(recommendation);
  } catch (error: unknown) {
    const message: string = error instanceof Error ? error.message : 'Unknown error';
    logger.warn('ATA classification did not return a recommendation', {
      defectId: task.defectId,
      error: message,
    });
    if (task.isActive()) task.onFailure();
  } finally {
    if (task.isActive()) task.onFinished();
  }
}

interface ClassificationState extends Omit<UseAtaClassificationResult, 'requestRecommendations'> {
  requestedDefectRequestsRef: MutableRefObject<Map<number, string>>;
  classificationSessionRef: MutableRefObject<number>;
  setPendingDefectIds: Dispatch<SetStateAction<number[]>>;
  setUnconfirmedDefectIds: Dispatch<SetStateAction<number[]>>;
  setFailedDefectIds: Dispatch<SetStateAction<number[]>>;
  setSettledDefectIds: Dispatch<SetStateAction<number[]>>;
}

function useClassificationState(isOpen: boolean): ClassificationState {
  const requestedDefectRequestsRef = useRef<Map<number, string>>(new Map<number, string>());
  const classificationSessionRef = useRef<number>(0);
  const [pendingDefectIds, setPendingDefectIds] = useState<number[]>([]);
  const [unconfirmedDefectIds, setUnconfirmedDefectIds] = useState<number[]>([]);
  const [failedDefectIds, setFailedDefectIds] = useState<number[]>([]);
  const [settledDefectIds, setSettledDefectIds] = useState<number[]>([]);
  const acknowledgeRecommendation = (defectId: number): void => {
    setUnconfirmedDefectIds((previous: number[]): number[] => removeDefectId(previous, defectId));
    setFailedDefectIds((previous: number[]): number[] => removeDefectId(previous, defectId));
  };
  const discardRecommendation = (defectId: number): void => {
    requestedDefectRequestsRef.current.delete(defectId);
    setPendingDefectIds((previous: number[]): number[] => removeDefectId(previous, defectId));
    setUnconfirmedDefectIds((previous: number[]): number[] => removeDefectId(previous, defectId));
    setFailedDefectIds((previous: number[]): number[] => removeDefectId(previous, defectId));
    setSettledDefectIds((previous: number[]): number[] => removeDefectId(previous, defectId));
  };
  useEffect((): void => {
    if (isOpen) return;
    classificationSessionRef.current += 1;
    requestedDefectRequestsRef.current.clear();
    setPendingDefectIds([]);
    setUnconfirmedDefectIds([]);
    setFailedDefectIds([]);
    setSettledDefectIds([]);
  }, [isOpen]);
  return {
    pendingDefectIds,
    unconfirmedDefectIds,
    failedDefectIds,
    settledDefectIds,
    acknowledgeRecommendation,
    discardRecommendation,
    requestedDefectRequestsRef,
    classificationSessionRef,
    setPendingDefectIds,
    setUnconfirmedDefectIds,
    setFailedDefectIds,
    setSettledDefectIds,
  };
}

interface TaskStartOptions {
  formData: UnscheduledEvent;
  availableAtaCodes: AtaCodeRecord[];
  selectedChapters: Record<number, string>;
  selectedSections: Record<number, string>;
  setSelectedChapters: Dispatch<SetStateAction<Record<number, string>>>;
  setSelectedSections: Dispatch<SetStateAction<Record<number, string>>>;
}

/**
 * Applies an accepted recommendation to the defect's ATA selections and flags it
 * as awaiting reviewer acknowledgement.
 */
function applyRecommendation(
  defect: Defect,
  recommendation: AtaClassificationRecommendation,
  options: TaskStartOptions,
  state: ClassificationState,
): void {
  options.setSelectedChapters(
    (previous: Record<number, string>): Record<number, string> => ({
      ...previous,
      [defect.id]: recommendation.chapter,
    }),
  );
  options.setSelectedSections(
    (previous: Record<number, string>): Record<number, string> => ({
      ...previous,
      [defect.id]: recommendation.section,
    }),
  );
  state.setUnconfirmedDefectIds((previous: number[]): number[] => addDefectId(previous, defect.id));
  state.setFailedDefectIds((previous: number[]): number[] => removeDefectId(previous, defect.id));
}

/** Clears the pending marker and records the defect as settled once a task ends. */
function finishClassificationTask(
  defect: Defect,
  state: ClassificationState,
  onSettled: () => void,
): void {
  onSettled();
  state.setPendingDefectIds((previous: number[]): number[] => removeDefectId(previous, defect.id));
  state.setSettledDefectIds((previous: number[]): number[] => addDefectId(previous, defect.id));
}

function startClassificationTask(
  defect: Defect,
  options: TaskStartOptions,
  state: ClassificationState,
  carrier: string,
  aircraftType: string,
  isActive: () => boolean,
  onSettled: () => void,
): void {
  state.setPendingDefectIds((previous: number[]): number[] => addDefectId(previous, defect.id));
  state.setSettledDefectIds((previous: number[]): number[] => removeDefectId(previous, defect.id));
  void runClassificationTask({
    defectId: defect.id,
    request: buildRequest(
      defect,
      options.formData,
      carrier,
      aircraftType,
      options.selectedChapters,
      options.selectedSections,
    ),
    availableAtaCodes: options.availableAtaCodes,
    isActive,
    onRecommendation: (recommendation: AtaClassificationRecommendation): void =>
      applyRecommendation(defect, recommendation, options, state),
    onFailure: (): void =>
      state.setFailedDefectIds((previous: number[]): number[] => addDefectId(previous, defect.id)),
    onFinished: (): void => finishClassificationTask(defect, state, onSettled),
  });
}

interface ClassificationRequestConfiguration {
  carrier: string;
  aircraftType: string;
  taskOptions: TaskStartOptions;
}

function createClassificationRequestConfiguration(
  options: UseAtaClassificationOptions,
): ClassificationRequestConfiguration | null {
  const {
    isOpen,
    mode,
    recordType,
    formData,
    availableAtaCodes,
    fleetData,
    selectedChapters,
    selectedSections,
    setSelectedChapters,
    setSelectedSections,
  } = options;
  if (
    !isOpen ||
    !formData ||
    mode === 'view' ||
    recordType === 'Scheduled' ||
    availableAtaCodes.length === 0
  )
    return null;
  const carrier = formData.carrier || getOperatorByTail(fleetData, formData.tailNumber);
  const aircraftType =
    formData.aircraftType || getAircraftTypeByTail(fleetData, formData.tailNumber);
  if (!formData.tailNumber || !carrier || !aircraftType) return null;
  return {
    carrier,
    aircraftType,
    taskOptions: {
      formData,
      availableAtaCodes,
      selectedChapters,
      selectedSections,
      setSelectedChapters,
      setSelectedSections,
    },
  };
}

interface ClassificationTaskBatch {
  triageRecommendationDefectIds: number[];
  defects: Defect[];
  state: ClassificationState;
  configuration: ClassificationRequestConfiguration;
  sessionId: number;
  isModalOpen: () => boolean;
  inFlightDefectIds: Set<number>;
}

function createClassificationTaskKey(
  defect: Defect,
  configuration: ClassificationRequestConfiguration,
  sessionId: number,
): string {
  return [
    sessionId,
    defect.id,
    defect.text.trim(),
    configuration.taskOptions.formData.tailNumber,
    configuration.carrier,
    configuration.aircraftType,
  ].join('\u0000');
}

function startNewClassificationTasks(batch: ClassificationTaskBatch): void {
  const {
    triageRecommendationDefectIds,
    defects,
    state,
    configuration,
    sessionId,
    isModalOpen,
    inFlightDefectIds,
  } = batch;
  triageRecommendationDefectIds.forEach((defectId: number): void => {
    const defect = defects.find((candidate: Defect): boolean => candidate.id === defectId);
    const taskKey = defect ? createClassificationTaskKey(defect, configuration, sessionId) : '';
    if (
      !defect ||
      !defect.text.trim() ||
      state.requestedDefectRequestsRef.current.get(defectId) === taskKey
    )
      return;
    state.requestedDefectRequestsRef.current.set(defectId, taskKey);
    inFlightDefectIds.add(defectId);
    startClassificationTask(
      defect,
      configuration.taskOptions,
      state,
      configuration.carrier,
      configuration.aircraftType,
      (): boolean =>
        isModalOpen() && state.requestedDefectRequestsRef.current.get(defectId) === taskKey,
      (): void => {
        inFlightDefectIds.delete(defectId);
      },
    );
  });
}

function useClassificationRequests(
  options: UseAtaClassificationOptions,
  state: ClassificationState,
): (defectIds: number[]) => boolean {
  const stateRef = useRef<ClassificationState>(state);
  const isModalOpenRef = useRef<boolean>(options.isOpen);
  const optionsRef = useRef<UseAtaClassificationOptions>(options);
  stateRef.current = state;
  isModalOpenRef.current = options.isOpen;
  optionsRef.current = options;

  const requestRecommendations = (defectIds: number[]): boolean => {
    const currentOptions = optionsRef.current;
    const configuration = createClassificationRequestConfiguration(currentOptions);
    if (!configuration || defectIds.length === 0) return false;
    startNewClassificationTasks({
      triageRecommendationDefectIds: defectIds,
      defects: currentOptions.defects,
      state: stateRef.current,
      configuration,
      sessionId: stateRef.current.classificationSessionRef.current,
      isModalOpen: (): boolean => isModalOpenRef.current,
      inFlightDefectIds: new Set<number>(),
    });
    return true;
  };

  useEffect((): void => {
    requestRecommendations(options.triageRecommendationDefectIds);
  }, [
    options.availableAtaCodes,
    options.defects,
    options.fleetData,
    options.formData,
    options.isOpen,
    options.mode,
    options.recordType,
    options.selectedChapters,
    options.selectedSections,
    options.setSelectedChapters,
    options.setSelectedSections,
    options.triageRecommendationDefectIds,
  ]);

  return requestRecommendations;
}

/** Runs one immediate KB ATA request per Triage-added defect after fleet and ATA data resolve. */
export function useAtaClassification(
  options: UseAtaClassificationOptions,
): UseAtaClassificationResult {
  const state = useClassificationState(options.isOpen);
  const requestRecommendations = useClassificationRequests(options, state);
  return {
    pendingDefectIds: state.pendingDefectIds,
    unconfirmedDefectIds: state.unconfirmedDefectIds,
    failedDefectIds: state.failedDefectIds,
    settledDefectIds: state.settledDefectIds,
    acknowledgeRecommendation: state.acknowledgeRecommendation,
    discardRecommendation: state.discardRecommendation,
    requestRecommendations,
  };
}
